import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { BOOKS_DATA } from '@/domains/mobile-web/constant/listBook.constant';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import { useCartCount } from '@/domains/mobile-web/hooks/useCart';
import CardProductTransaction, {
    type TransactionOrder,
} from '@/domains/mobile-web/components/CardProductTransaction/CardProductTransaction';
import Button from '@/shared/components/ui/Button';
import { Copy, Navigation, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';



interface TransactionDetailProps {
    onCopy?: (value: string, transaction: TransactionOrder) => void;
    onTrack?: (transaction: TransactionOrder) => void;
    onReceived?: (transaction: TransactionOrder) => void;
    onBuyAgain?: (transaction: TransactionOrder) => void;
}


const LEBAH_KECIL = BOOKS_DATA[1]!;

const TRANSACTION: TransactionOrder = {
    id: 'trx-1',
    invoice: 'INV/20241024/TKB/89421',
    date: '9 Sept 2026',
    status: 'delivery',
    items: [
        { product: LEBAH_KECIL, quantity: 1 },
    ],
    totalPayment: 4157900,
    shipping: {
        courier: 'J&T Express',
        trackingNumber: 'JT9823410924ID',
    },
};

const SHIPPING_COST = 100000;


const copyToClipboard = async (value: string) => {
    try {
        await navigator.clipboard?.writeText(value);
    } catch {

    }
};

const formatPrice = (price: number) => new Intl.NumberFormat('id-ID').format(price);

const TransactionDetail = ({ onCopy, onTrack, onReceived, onBuyAgain }: TransactionDetailProps) => {
    const navigate = useNavigate();
    const cartCount = useCartCount();
    const [copied, setCopied] = useState<string | null>(null);
    const transaction = TRANSACTION;
    const { status, shipping, totalPayment } = transaction;

    const handleCopy = (value: string) => {
        copyToClipboard(value);
        onCopy?.(value, transaction);
        setCopied(value);
        window.setTimeout(() => setCopied((prev) => (prev === value ? null : prev)), 1500);
    };

    const isProcess = status === 'process';
    const isDelivery = status === 'delivery';
    const isFinish = status === 'finish';
    const trackingNumber = shipping?.trackingNumber ?? '-';

    return (
        <main className="min-h-screen">
            <HeaderMobile>
                <TopBarHeader
                    type="textBucket"
                    onBack={() => navigate(-1)}
                    totalProduct={cartCount}
                    onAction={() => navigate('/store/cart')}
                    title="Detail Transaksi"
                />
            </HeaderMobile>

            <WrapperMobile className='mb-[60px]'>
                <CardProductTransaction transaction={transaction} variant="compact" />
                <div className="shadowCard p-3 flex flex-col gap-3">
                    <h3 className='title2medium text-black'>Informasi Pengiriman</h3>
                    <div className="grid grid-cols-[100px_1fr] gap-3 justify-between">
                        <span className='reg12 text-text-grey'>Pengiriman</span>
                        <span>{shipping?.courier ?? '-'}</span>
                    </div>
                    <div className="grid grid-cols-[100px_1fr] gap-3 justify-between">
                        <span className='reg12 text-text-grey'>Nomor Resi</span>
                        {shipping ? (
                            <button
                                type="button"
                                onClick={() => handleCopy(trackingNumber)}
                                aria-label={`Salin nomor resi ${trackingNumber}`}
                                className="flex w-fit shrink-0 items-center gap-1 body1medium text-text-blue-100 transition hover:opacity-80"
                            >
                                <Copy size={14} strokeWidth={1.8} />
                                <span>{copied === trackingNumber ? 'Tersalin' : trackingNumber}</span>
                            </button>
                        ) : (
                            <span>-</span>
                        )}
                    </div>
                    <div className="grid grid-cols-[100px_1fr] gap-3 justify-between">
                        <span className='reg12 text-text-grey'>Alamat </span>
                        <div className="text-text-grey">
                            <p className='text-black'>Budi Santoso</p>
                            <p>+62 812-3456-7890</p>
                            <p>Jl. Margonda Raya No. 123, Blok A, Lantai 2, Depok, Jawa Barat</p>
                            <p>16432</p>

                        </div>
                    </div>
                </div>
                <div className="shadowCard p-3 flex flex-col gap-3">
                    <h3 className='title2medium text-black'>Rincian Pembayaran</h3>
                    <div className="inline-flex gap-3 justify-between items-baseline flex-wrap">
                        <span className='reg12 text-text-grey'>Metode Pembayaran</span>
                        <span>BCA Virtual Account</span>
                    </div>
                    <div className="inline-flex gap-3 justify-between items-baseline flex-wrap">
                        <span className='reg12 text-text-grey'>Total Harga</span>
                        <span>Rp {formatPrice(totalPayment)}</span>
                    </div>
                    <div className="inline-flex gap-3 justify-between items-baseline flex-wrap">
                        <span className='reg12 text-text-grey'>Ongkos Kirim</span>
                        <span>Rp {formatPrice(SHIPPING_COST)}</span>
                    </div>
                    <div className="border-blue-cs-200 border-[0.5px]"></div>
                    <div className="inline-flex gap-3 justify-between items-baseline flex-wrap">
                        <span className='title1bold text-black'>Total Bayar</span>
                        <span className='title1bold text-black'>Rp {formatPrice(totalPayment + SHIPPING_COST)}</span>
                    </div>
                </div>

            </WrapperMobile>
            <div className="fixed inset-x-0 bottom-0 z-10 mx-auto flex w-full items-center gap-3 bg-white px-6 py-3 lg:max-w-[60%] md:max-w-[80%]">
                {isFinish ? (
                    <Button
                        type="button"
                        onClick={() => onBuyAgain?.(transaction)}
                        customButton='text-[16px] h-[45px]!'
                        className=" w-full rounded-[8px] bg-text-blue-100 text-white text-[16px] hover:bg-text-blue-100/90"
                    >
                        <RotateCcw size={16} strokeWidth={2} />
                        Beli Lagi
                    </Button>
                ) : (
                    <>
                        <Button
                            type="button"
                            disabled={isProcess}
                            onClick={() => onTrack?.(transaction)}
                            customButton='text-[16px] h-[45px]!'
                            className={cn(
                                'flex-1 rounded-[8px]',
                                isDelivery
                                    ? 'border border-blue-cs-200 bg-white text-black hover:bg-[#F3F8FF]'
                                    : 'bg-text-grey'
                            )}
                        >
                            <Navigation size={16} strokeWidth={2} />
                            Lacak
                        </Button>
                        <Button
                            type="button"
                            disabled={isProcess}
                            onClick={() => onReceived?.(transaction)}
                            customButton='text-[16px] h-[45px]!'
                            className={cn(
                                ' flex-1 rounded-[8px]',
                                isDelivery
                                    ? 'bg-text-blue-100 text-white hover:bg-text-blue-100/90'
                                    : 'bg-text-grey'
                            )}
                        >
                            Diterima
                        </Button>
                    </>
                )}
            </div>
        </main>
    );
};

export default TransactionDetail;
